import { Box, IconButton, Tooltip, Popover, Paper, Typography } from "@mui/material";
import { MicIcon, MicOffIcon, SettingsIcon } from "lucide-react";
import React, { useState } from "react";
import { useAppSelector } from "../../store";
import { useAppDispatch } from "../../store";
import { selectAvatarState, selectCurrentSessionWithDetails, selectSpeechState } from "../../store/selectors";
import {
    toggle,
  } from "../../store/slices/speechSlice";
import { MicrophoneSelector } from "../MicrophoneSelector";

export const SoundController : React.FC = () => {
    const dispatch = useAppDispatch();
    const speechState = useAppSelector(selectSpeechState);
    const sessionDetails = useAppSelector(selectCurrentSessionWithDetails);
    const avatarState = useAppSelector(selectAvatarState);
    const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);

    const handleSpeechToggle = () => {
        if (!speechState.isEnabled) {
            console.log("Speech recognition not enabled");
            return;
          }
          dispatch(toggle());
    }

    const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
        setSettingsAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsAnchorEl(null);
    };

    const isSpeechDisabled = !speechState.isEnabled || speechState.isConnecting || !sessionDetails || !avatarState.isConnected;

    const getSpeechTooltip = () => {
        if (!avatarState.isConnected) {
            return "Connect Avatar to enable speech recognition";
        }

        if (!speechState.isEnabled) {
            return "Speech recognition not available";
        }

        if (speechState.isConnecting) {
            return "Connecting speech recognition...";
        }

        if (speechState.isListening) {
            return "Click to mute microphone";
        }
        return "Click to unmute microphone";
    }

    const isSettingsOpen = Boolean(settingsAnchorEl);

    return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 10,
        right: 10, 
      }}
    >
         <Tooltip title={getSpeechTooltip()} placement="bottom">
          <Box sx={{ backgroundColor: "rgba(0, 0, 0, 0.47)", borderRadius: "10px", padding: "5px 5px", display: 'flex', gap: '4px' }}>
            <IconButton
              onClick={handleSpeechToggle}
              disabled={isSpeechDisabled}
              size="small"
              sx={{
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                  color: "#FFFFFF",
                },
              }}
            >
              {speechState.isListening ? <MicIcon /> : <MicOffIcon />}
            </IconButton>
            
            <Tooltip title="Microphone Settings" placement="bottom">
              <IconButton
                onClick={handleSettingsClick}
                size="small"
                sx={{
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <SettingsIcon size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </Tooltip>
        
        <Popover
          open={isSettingsOpen}
          anchorEl={settingsAnchorEl}
          onClose={handleSettingsClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 250 }}>
            <Typography variant="h6" gutterBottom>
              Microphone Settings
            </Typography>
            <MicrophoneSelector size="small" variant="outlined" />
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
              Changes will apply when you restart speech recognition
            </Typography>
          </Paper>
        </Popover>
    </Box>
  );
};